import { useQueryClient } from "@tanstack/react-query";
import React, { useMemo, useState } from "react";

import { ButtonStandard, ModalBase, Spinner, Text, toast } from "@performyard/py-components";

import { type EmployeeValue, SingleSelectEmployee } from "../../../../components/Inputs/SelectEmployee";
import { createFetchEmployeesForPicker } from "../../../../components/Inputs/SelectEmployee/shared";

import { useAssignActingManager } from "../../../../api/hooks";
import { useUser } from "../../../../api/hooks/defs/user";

const normalizeId = (id: string): string => id.trim().toLowerCase();

/**
 * Build the list of selectable acting-manager candidates.
 *
 * When an acting manager is already assigned, the only option is the original
 * budget manager (so the user can "unset" by re-selecting them).
 *
 * Otherwise, the only candidate is the budget manager's directory manager.
 * The budget manager themselves is excluded from the assignment flow to
 * prevent a no-op selection. If the manager points back to themselves,
 * a single hasNoManager-entry is returned so the "listed as their own manager"
 * error is shown.
 */

type ExtendedEmployeeValue = {
	id: string;
	name: string;
	hasNoManager: boolean;
};

const getActingManagerCandidates = (
	budgetManager: EmployeeValue,
	lineManagerProfile: { id: string; manager?: { id: string; name: string } | null } | undefined,
	assignedActingManager: EmployeeValue | undefined
): ExtendedEmployeeValue[] => {
	if (assignedActingManager != null) {
		return [{ id: budgetManager.id.trim(), name: budgetManager.name, hasNoManager: false }];
	}

	if (!lineManagerProfile) {
		return [];
	}

	const parent = lineManagerProfile.manager;
	const lineManagerId = normalizeId(lineManagerProfile.id);

	// No manager assigned in the directory, or manager points back to
	// themselves — return only the hasNoManager entry so the "listed as their own
	// manager" error is shown via the onlyCandidateHasNoManager check.
	if (!parent || normalizeId(parent.id) === lineManagerId) {
		return [{ id: budgetManager.id.trim(), name: budgetManager.name, hasNoManager: !parent }];
	}

	// Normal case: only offer the directory manager as a candidate.
	// The budget manager themselves is excluded to prevent a misleading
	// no-op "unassign" when nothing was assigned yet.
	return [{ id: parent.id.trim(), name: parent.name, hasNoManager: false }];
};

export type ActingManagerModalProps = {
	isOpen: boolean;
	onClose: () => void;
	budgetManager: EmployeeValue;
	compensationCycleId: string;
	assignedActingManager?: EmployeeValue;
};

export const ActingManagerModal = ({
	isOpen,
	onClose,
	budgetManager,
	compensationCycleId,
	assignedActingManager,
}: ActingManagerModalProps): React.ReactElement => {
	const queryClient = useQueryClient();
	const [selection, setSelection] = useState<EmployeeValue | null>(assignedActingManager ?? null);

	const { mutate, isPending } = useAssignActingManager(compensationCycleId);

	const { data: lineManagerProfile, isLoading: isLoadingProfile } = useUser({
		userID: budgetManager.id,
		disabled: !isOpen || assignedActingManager != null,
	});

	const candidates = useMemo(
		() => getActingManagerCandidates(budgetManager, lineManagerProfile, assignedActingManager),
		[budgetManager, lineManagerProfile, assignedActingManager]
	);

	const defaultEmployeeSearch = useMemo(() => createFetchEmployeesForPicker(queryClient), [queryClient]);

	const fetchCandidates = useMemo(() => {
		if (!budgetManager) {
			return defaultEmployeeSearch;
		}
		const selectableCandidates = candidates.filter(
			(e) => assignedActingManager != null || normalizeId(e.id) !== normalizeId(budgetManager.id)
		);
		return async (searchTerm: string): Promise<EmployeeValue[]> => {
			const q = searchTerm.toLowerCase();
			return selectableCandidates.filter((e) => e.name.toLowerCase().includes(q));
		};
	}, [budgetManager, defaultEmployeeSearch, candidates, assignedActingManager]);

	const isResolvingCandidates = isOpen && assignedActingManager == null && isLoadingProfile;

	const onlyCandidateHasNoManager =
		isOpen &&
		assignedActingManager == null &&
		candidates.length === 1 &&
		normalizeId(candidates[0].id) === normalizeId(budgetManager.id) &&
		!candidates[0].hasNoManager;

	const hasNoCandidates =
		isOpen &&
		assignedActingManager == null &&
		candidates.length === 1 &&
		normalizeId(candidates[0].id) === normalizeId(budgetManager.id) &&
		candidates[0].hasNoManager;
	const handleClose = (): void => {
		onClose();
		setSelection(null);
	};

	const handleAssign = (): void => {
		if (!selection) return;

		mutate(
			{
				currentManagerId: budgetManager.id,
				actingManagerId: selection.id,
			},
			{
				onSuccess: () => {
					handleClose();
					const isUnset = budgetManager.id === selection.id;
					toast.success(isUnset ? "Unassigned acting manager" : "Acting manager assigned");
				},
			}
		);
	};
	if (isResolvingCandidates) {
		return (
			<ModalBase isOpen={isOpen} onClose={handleClose} dangerouslySetClassName="w-[432px] pb-[10px]">
				<div className="flex items-center justify-center py-12">
					<Spinner size={32} />
				</div>
			</ModalBase>
		);
	}
	if (onlyCandidateHasNoManager) {
		return (
			<ModalBase isOpen={isOpen} onClose={handleClose} dangerouslySetClassName="w-[432px] pb-[10px]">
				<div className="mx-auto box-border flex w-[384px] min-w-0 flex-col px-[16px]">
					<div className="flex w-full min-w-0 flex-col pt-4 text-center">
						<Text size="sub-heading" weight="semi-bold" dangerouslySetClassName="m-0">
							Cannot update
						</Text>
						<Text
							size="paragraph-2"
							weight="regular"
							dangerouslySetClassName="m-0 min-w-0 whitespace-normal break-words"
						>
							This employee is listed as their own manager.
						</Text>
					</div>
				</div>
				<div className="mt-6 flex w-full flex-row items-center justify-center pb-6">
					<ButtonStandard
						color="primary"
						size="large"
						type="button"
						dangerouslySetClassName="!w-[90px] shrink-0"
						onClick={handleClose}
					>
						Close
					</ButtonStandard>
				</div>
			</ModalBase>
		);
	}

	if (hasNoCandidates) {
		return (
			<ModalBase isOpen={isOpen} onClose={handleClose} dangerouslySetClassName="w-[432px] pb-[10px]">
				<div className="mx-auto box-border flex w-[384px] min-w-0 flex-col px-[16px]">
					<div className="flex w-full min-w-0 flex-col pt-4 text-center">
						<Text size="sub-heading" weight="semi-bold" dangerouslySetClassName="m-0">
							This employee does not have a manager
						</Text>
						<Text
							size="paragraph-2"
							weight="regular"
							dangerouslySetClassName="m-0 min-w-0 whitespace-normal break-words"
						>
							Assign a manager to this employee before delegating merit decisions.
						</Text>
					</div>
				</div>
				<div className="mt-6 flex w-full flex-row items-center justify-center pb-6">
					<ButtonStandard
						color="primary"
						size="large"
						type="button"
						dangerouslySetClassName="!w-[90px] shrink-0"
						onClick={handleClose}
					>
						Close
					</ButtonStandard>
				</div>
			</ModalBase>
		);
	}

	return (
		<ModalBase isOpen={isOpen} onClose={handleClose} dangerouslySetClassName="w-[432px] pb-[10px]">
			<div className="mx-auto box-border flex w-[384px] min-w-0 flex-col px-[16px]">
				<div className="flex w-full min-w-0 flex-col gap-2 pt-4 text-center">
					<Text size="sub-heading" weight="semi-bold" dangerouslySetClassName="m-0">
						Assign an Acting Manager
					</Text>
					<Text
						size="paragraph-2"
						weight="regular"
						dangerouslySetClassName="m-0 min-w-0 whitespace-normal break-words"
					>
						This action will delegate the merit decisions of this budget to the selected manager.
					</Text>
				</div>
				<div className="w-full min-w-0 pb-2 pt-4">
					<SingleSelectEmployee
						id="budget-summary-acting-manager-select"
						dangerouslySetClassName="!w-full min-w-0 max-w-full"
						value={selection ?? undefined}
						onChange={(employeeId, employeeName) => {
							if (employeeId && employeeName) {
								setSelection({ id: employeeId, name: employeeName });
							} else {
								setSelection(null);
							}
						}}
						fetchEmployees={fetchCandidates}
						placeholder="Select acting manager..."
						isClearable={false}
						minOptionsForCombobox={8}
					/>
				</div>
				<div className="flex w-full flex-row items-center justify-center gap-2 pb-6 pt-2">
					<ButtonStandard
						variant="outline"
						color="neutral"
						size="medium"
						type="button"
						dangerouslySetClassName="!w-[90px] shrink-0"
						onClick={handleClose}
					>
						Cancel
					</ButtonStandard>
					<ButtonStandard
						color="primary"
						size="medium"
						type="button"
						dangerouslySetClassName="!w-[90px] shrink-0"
						disabled={!selection || isPending}
						onClick={handleAssign}
					>
						Assign
					</ButtonStandard>
				</div>
			</div>
		</ModalBase>
	);
};
